// DefectAlertsButton: top-bar callout for defect discussion activity.
// Collapsed: a chat icon with an unread-total badge. Click: popover listing the
// defects with unread messages (row click opens that thread on the /defects
// inbox). Data comes from GET defect/unread; refreshes on mount, window focus,
// a slow 60s interval, and instantly on the socket's defect:activity ping
// (debounced, since a burst of messages fires one ping each).
// Mounted in AppShell gated canAddDefect OR canInspectAsset.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import moment from 'moment';
import { baseUrl } from '../../shared';
import { getDefectSocket } from './defectSocket';
import DefectChatModal from './DefectChatModal';
import ResolveDefectDialog from './ResolveDefectDialog';

const REFRESH_MS = 60000;
const MAX_POPOVER_ROWS = 8;
// "Your recent discussions": open defects the caller commented on inside this
// window, capped at MAX_RECENT_ROWS. The window is deliberately 7 days, not
// 24h: customer replies run on a next-day-plus cadence, so a day window would
// usually be empty. The row cap is what keeps the list short.
const RECENT_DAYS = 7;
const MAX_RECENT_ROWS = 5;

// One defect row in the popover, shared by the unread and recent sections
// (the "N new" pill only renders when there is something unread).
function PopoverRow({ defect, onClick }) {
  return (
    <Box onClick={onClick} sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { background: 'var(--surface-2)' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" className="aims-mono" sx={{ fontWeight: 600, color: 'var(--text)' }}>
          {defect.assetId}
        </Typography>
        {defect.unreadCount > 0 && (
          <Box component="span" sx={{ background: 'var(--primary)', color: '#fff', borderRadius: '10px', px: 0.75, py: 0.1, fontSize: 11, fontWeight: 700, lineHeight: 1.6, flexShrink: 0 }}>
            {defect.unreadCount} new
          </Box>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'var(--text)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {defect.defectComment}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--muted)' }}>
        {defect.lastMessageAt ? moment(defect.lastMessageAt).format('DD-MM-YYYY hh:mm A') : ''}
      </Typography>
    </Box>
  );
}

export default function DefectAlertsButton() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [defects, setDefects] = useState([]);
  // Thread opened from the popover: renders on top of whatever page is showing
  // (this component lives in AppShell), no navigation involved.
  const [chatDefectId, setChatDefectId] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(() => {
    fetch(baseUrl + 'defect/unread', {
      headers: {
        username: localStorage.getItem('username'),
        bToken: localStorage.getItem('bToken'),
      },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((json) => setDefects(Array.isArray(json) ? json : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    window.addEventListener('focus', load);

    const socket = getDefectSocket();
    const onActivity = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(load, 500);
    };
    if (socket) socket.on('defect:activity', onActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', load);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (socket) socket.off('defect:activity', onActivity);
    };
  }, [load]);

  const unreadDefects = defects.filter((d) => d.unreadCount > 0);
  const totalUnread = unreadDefects.reduce((sum, d) => sum + (d.unreadCount || 0), 0);
  // Open defects the caller recently commented on, excluding ones already
  // shown in the unread section above.
  const recentCutoff = moment().subtract(RECENT_DAYS, 'days');
  const recentDefects = defects
    .filter((d) => !d.resolvedAt && d.unreadCount === 0 && d.lastMyMessageAt && moment(d.lastMyMessageAt).isAfter(recentCutoff))
    .slice(0, MAX_RECENT_ROWS);
  const open = Boolean(anchorEl);

  // Row click: open the thread in place. "View all" is the only navigation.
  const openThread = (defectId) => {
    setAnchorEl(null);
    setChatDefectId(defectId);
  };
  const openInbox = () => {
    setAnchorEl(null);
    navigate('/defects');
  };
  const openAsset = (defect) => {
    setChatDefectId(null);
    navigate('/assets?assetNo=' + encodeURIComponent(defect.assetId));
  };

  return (
    <>
      <Tooltip title="Defect discussions">
        <IconButton onClick={(e) => { setAnchorEl(e.currentTarget); load(); }} sx={{ color: 'var(--on-rail)' }} aria-label="defect discussions">
          <Badge badgeContent={totalUnread} color="error" max={99}>
            <ForumOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>
            Defect discussions
          </Typography>
          <Button size="small" onClick={() => openInbox()} sx={{ textTransform: 'none', fontWeight: 700, p: 0, minWidth: 0 }}>
            View all
          </Button>
        </Box>
        <Box sx={{ py: 0.5, maxHeight: 400, overflowY: 'auto' }}>
          {unreadDefects.length === 0 && recentDefects.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'var(--muted)', px: 2, display: 'block', py: 1.5 }}>
              No unread defect activity.
            </Typography>
          ) : (
            <>
              {unreadDefects.slice(0, MAX_POPOVER_ROWS).map((d) => (
                <PopoverRow key={d.id} defect={d} onClick={() => openThread(d.id)} />
              ))}
              {unreadDefects.length > MAX_POPOVER_ROWS && (
                <Typography variant="caption" sx={{ color: 'var(--muted)', px: 2, display: 'block', py: 0.75 }}>
                  {unreadDefects.length - MAX_POPOVER_ROWS} more with unread activity. View all defects to see them.
                </Typography>
              )}
              {recentDefects.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600, px: 2, display: 'block', pt: 1, pb: 0.25, borderTop: unreadDefects.length > 0 ? '1px solid var(--border)' : 'none', mt: unreadDefects.length > 0 ? 0.5 : 0 }}>
                    Your recent discussions · {RECENT_DAYS} days
                  </Typography>
                  {recentDefects.map((d) => (
                    <PopoverRow key={d.id} defect={d} onClick={() => openThread(d.id)} />
                  ))}
                </>
              )}
            </>
          )}
        </Box>
      </Popover>

      <DefectChatModal
        open={chatDefectId !== null}
        defectId={chatDefectId}
        onClose={() => setChatDefectId(null)}
        onMarkComplete={(id) => {
          const target = defects.find((d) => d.id === id);
          if (target) setResolveTarget(target);
        }}
        onOpenAsset={openAsset}
        onReportNew={() => {
          const target = defects.find((d) => d.id === chatDefectId);
          if (target) openAsset(target);
        }}
        onChanged={load}
      />
      <ResolveDefectDialog
        defect={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onResolved={load}
      />
    </>
  );
}
